import { gsap } from "gsap";

const menuCloseCustom = () => {
	const nav = document.querySelector("#menu-principal .wp-block-navigation");
	if (!nav) return;

	const container = nav.querySelector(
		".wp-block-navigation__responsive-container"
	);
	const openBtn = nav.querySelector(
		".wp-block-navigation__responsive-container-open"
	);
	const closeBtn = nav.querySelector(
		".wp-block-navigation__responsive-container-close"
	);
	const contentDiv = nav.querySelector(
		".wp-block-navigation__responsive-container-content"
	);
	const ul = contentDiv
		? contentDiv.querySelector("ul.wp-block-navigation__container, ul")
		: null;

	if (!closeBtn) return;

	// Create or locate the vertical dividing line before the ul
	let verticalLine = null;
	if (contentDiv && ul) {
		verticalLine = contentDiv.querySelector(".menu-boundary-line");
		if (!verticalLine) {
			verticalLine = document.createElement("div");
			verticalLine.classList.add("menu-boundary-line");
			verticalLine.setAttribute("aria-hidden", "true");
			contentDiv.insertBefore(verticalLine, ul);
		}
	}

	// Check if the button already has an SVG icon for the X; if not, add it.
	if (!closeBtn.querySelector("svg") && !closeBtn.querySelector("img")) {
		closeBtn.innerHTML = `
			<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">
				<line x1="18" y1="6" x2="6" y2="18"></line>
				<line x1="6" y1="6" x2="18" y2="18"></line>
			</svg>
		`;
	}

	// Verify exclusive support for screens ≥ 1279px and mouse/fine pointer.
	const isDesktopScreen = () =>
		window.matchMedia("(min-width: 1279px) and (pointer: fine) and (hover: hover)").matches;

	let isMenuOpen = false;
	let originX = 0;
	let originY = 0;
	let mouseX = window.innerWidth / 2;
	let mouseY = window.innerHeight / 2;
	let xTo = null;
	let yTo = null;

	// Function to safely close the menu
	const closeMenu = () => {
		if (container) {
			container.classList.remove("is-menu-open");
			container.classList.add("hidden-by-default");
			container.setAttribute("aria-hidden", "true");
		}
		document.documentElement.classList.remove("has-modal-open");
		document.body.classList.remove("has-modal-open");

		if (openBtn) {
			openBtn.setAttribute("aria-expanded", "false");
		}

		stopFollowing();
	};

	// Close button click event
	closeBtn.addEventListener("click", () => {
		closeMenu();
	});

	// Ensure the z-index is set so that it remains visible and accessible.
	closeBtn.style.zIndex = "100000";

	// Position the vertical line exactly at the left edge of the ul
	const updateLinePosition = () => {
		if (!verticalLine || !ul || !contentDiv) return;
		verticalLine.style.left = `${ul.offsetLeft}px`;
		verticalLine.style.top = `${ul.offsetTop}px`;
		verticalLine.style.height = `${ul.offsetHeight}px`;
	};

	// Recalculate the button's base center so that the offset is exact.
	const updateOrigin = () => {
		const currentX = gsap.getProperty(closeBtn, "x") || 0;
		const currentY = gsap.getProperty(closeBtn, "y") || 0;

		gsap.set(closeBtn, { x: 0, y: 0 });
		const rect = closeBtn.getBoundingClientRect();
		originX = rect.left + rect.width / 2;
		originY = rect.top + rect.height / 2;
		gsap.set(closeBtn, { x: currentX, y: currentY });

		updateLinePosition();
	};

	// Mouse movement handler (only >= 1279px)
	const handleMouseMove = (e) => {
		if (!isMenuOpen || !isDesktopScreen()) return;

		mouseX = e.clientX;
		mouseY = e.clientY;

		// Calculate the horizontal limit based on the left edge of the ul
		let boundaryX = window.innerWidth;
		if (ul) {
			const ulRect = ul.getBoundingClientRect();
			boundaryX = ulRect.left;
		}

		const btnHalfWidth = (closeBtn.offsetWidth || 50) / 2;
		// We leave a 10px margin before the line so as not to touch the ul
		const maxAllowedX = boundaryX - btnHalfWidth - 10;

		if (!xTo || !yTo) return;

		// The animation only follows the pointer if it is located to the left of the line stroke.
		if (mouseX <= boundaryX) {
			const targetMouseX = Math.min(mouseX, maxAllowedX);
			xTo(targetMouseX - originX);
			yTo(mouseY - originY);
		} else {
			// If the pointer moves to the right side (towards the ul), the button does not follow the cursor;
			// It stops exactly at the left edge of the line without touching the ul.
			xTo(maxAllowedX - originX);
		}
	};

	// Start mouse tracking when opening the menu (only >= 1279px)
	const startFollowing = (initialX, initialY) => {
		if (!isDesktopScreen()) {
			// On screens smaller than 1279px, we ensure that no inline transformation styles remain.
			gsap.killTweensOf(closeBtn);
			gsap.set(closeBtn, { clearProps: "transform,scale,opacity" });
			return;
		}

		if (isMenuOpen) return;
		isMenuOpen = true;

		// Kill previous animations and reset to the base state to take measurements.
		gsap.killTweensOf(closeBtn);
		gsap.set(closeBtn, { x: 0, y: 0, scale: 1, opacity: 1 });

		const rect = closeBtn.getBoundingClientRect();
		originX = rect.left + rect.width / 2;
		originY = rect.top + rect.height / 2;

		updateLinePosition();

		if (initialX !== undefined && initialY !== undefined) {
			mouseX = initialX;
			mouseY = initialY;
		}

		// Create the reactive tweens with a duration of 0.3s (the requested delay).
		xTo = gsap.quickTo(closeBtn, "x", {
			duration: 0.3,
			ease: "power2.out",
		});
		yTo = gsap.quickTo(closeBtn, "y", {
			duration: 0.3,
			ease: "power2.out",
		});

		// Calculate the starting position while respecting the line limit.
		let boundaryX = window.innerWidth;
		if (ul) {
			const ulRect = ul.getBoundingClientRect();
			boundaryX = ulRect.left;
		}
		const btnHalfWidth = (closeBtn.offsetWidth || 50) / 2;
		const maxAllowedX = boundaryX - btnHalfWidth - 10;
		const startX = Math.min(mouseX, maxAllowedX);

		xTo(startX - originX);
		yTo(mouseY - originY);

		window.addEventListener("mousemove", handleMouseMove, { passive: true });
	};

	// Stop tracking and reset the button when closing the menu
	const stopFollowing = () => {
		window.removeEventListener("mousemove", handleMouseMove);

		if (!isMenuOpen) {
			gsap.killTweensOf(closeBtn);
			gsap.set(closeBtn, { clearProps: "transform,scale,opacity" });
			return;
		}
		isMenuOpen = false;

		gsap.killTweensOf(closeBtn);

		// Animate the button back to its base position and clear transformations.
		gsap.to(closeBtn, {
			x: 0,
			y: 0,
			scale: 1,
			opacity: 1,
			duration: 0.3,
			ease: "power2.inOut",
			onComplete: () => {
				gsap.set(closeBtn, {
					clearProps: "transform,scale,opacity",
				});
				closeBtn.style.pointerEvents = "auto";
			},
		});
	};

	// Handle window resizing in real time
	const handleResize = () => {
		if (!isDesktopScreen()) {
			if (isMenuOpen) {
				stopFollowing();
			} else {
				gsap.killTweensOf(closeBtn);
				gsap.set(closeBtn, { clearProps: "transform,scale,opacity" });
			}
			return;
		}

		if (isMenuOpen) {
			updateOrigin();
		}
	};

	window.addEventListener("resize", handleResize);

	// Capture click coordinates to start moving toward the cursor immediately if the width is >= 1279px.
	if (openBtn) {
		openBtn.addEventListener("click", (e) => {
			if (isDesktopScreen()) {
				mouseX = e.clientX;
				mouseY = e.clientY;
			}
		});
	}

	// Monitor menu opening/closing via WordPress class changes.
	if (container) {
		const observer = new MutationObserver(() => {
			const isOpen =
				container.classList.contains("is-menu-open") ||
				document.documentElement.classList.contains("has-modal-open") ||
				document.body.classList.contains("has-modal-open");

			if (isOpen) {
				startFollowing(mouseX, mouseY);
			} else {
				stopFollowing();
			}
		});

		observer.observe(container, {
			attributes: true,
			attributeFilter: ["class", "aria-hidden"],
		});

		observer.observe(document.documentElement, {
			attributes: true,
			attributeFilter: ["class"],
		});
	}

	// Close using the Escape key as well.
	document.addEventListener("keydown", (e) => {
		if (e.key === "Escape" && isMenuOpen) {
			closeMenu();
		}
	});
};

export { menuCloseCustom };
